import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ImportsService } from '@src/app/shared/backend/clients';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ImportTypePipe } from '@src/app/shared/pipes/importType.pipe';
import { ImportState } from '@src/app/shared/enums/ImportState.enum';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { FiltersPanelChangeEvent, FiltersPanelFieldOverrides } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { ImportTypePipe as ImportTypeListPipe } from '@app/shared/pipes/importType.pipe';
import { ImportStatePipe } from '@src/app/shared/pipes/importState.pipe';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';

@Component({
    selector: 'app-imports',
    templateUrl: './imports.component.html',
    styleUrl: './imports.component.scss',
    host: {
        class: 'app-imports'
    },
    providers: [ImportTypePipe],
    standalone: false
})
export class ImportsComponent extends BaseListComponent implements OnInit {

  private initialLoadDone = false;
  private readonly componentDestroyed$ = new Subject<void>();
  public readonly importsTableName = TableSettingService.ImportsTableName;

  public filtersPanelFieldOverrides: FiltersPanelFieldOverrides = {};

  // Dropdown: import types
  private importTypeOptions: Array<{ id: string | number; text: string }> = [];

  // Dropdown: import states
  private importStateOptions: Array<{ id: string | number; text: string }> = [];

  displayImportDetailsModalbox: boolean = false;
  toasterService: any;
  modalboxDetails: any = null;

  allowCreateImport: boolean = false;
  allowDownloadImport: boolean = false;

  constructor(
    private router: Router,
    public override tableSettingService: TableSettingService,
    private importsService: ImportsService,
    private permissionService: PermissionService,
    public importTypePipe: ImportTypePipe,
    private translateService: TranslateService) {
    super();
  }

  async ngOnInit() {
    this.restorePaginatorStateFromStorage();

    this.allowCreateImport = await this.permissionService.hasActionPermissions(Actions.create_imports);
    this.allowDownloadImport = await this.permissionService.hasActionPermissions(Actions.import_download);

    // i18n propagation on lang chang
    this.rebuildLocalDropdownOptions();

    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.rebuildLocalDropdownOptions();
        this.rebuildFiltersPanelOverrides();

        // Update sort labels without resetting the current sort selection
        this.rebuildSortPanelColumnLabels();
      });

    await this.tableSettingService.init();
    await super.setCache(this.importsTableName);

    // default sort for stable pagination + 18n fn labelResolver (sort dropdown options)
    this.initSortPanel({
      defaultSort: { key: 'createdOn', direction: 'DESC' },
      labelResolver: (col) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        if (translated && translated !== translationKey) {
          return translated;
        }

        return col.header;
      }
    });

    this.rebuildSortPanelColumnLabels();
    this.rebuildFiltersPanelOverrides();

    // Fallback: if the panel doesn't restore anything, load unfiltered!!
    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.get();
      }
    });
  }

  override async get() {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.page = await firstValueFrom(this.importsService.apiImportsFilterTablePost(this.filter));
    this.data = this.page.results;
  }

  override onPageChange(event: any): void {
    this.initialLoadDone = true;
    super.onPageChange(event);
  }

  protected override onCriteriaResetRequested(): void {
    this.resetPaginatorStorage();
  }

  private resetPaginatorStorage(): void {
    resetPaginatorStateToFirstPage({
      tableName: this.importsTableName,
      pageSize: this.pageSize,
    });
  }

  private restorePaginatorStateFromStorage(): void {
    const stored = loadStoredPaginatorState(this.importsTableName);

    if (!stored) {
      return;
    }

    const storedPageNumber = stored.valuesByFieldKey.pageNumber;
    const storedPageSize = stored.valuesByFieldKey.pageSize;

    this.pageNumber = storedPageNumber;
    this.pageSize = storedPageSize;

    this.filter.pageNumber = storedPageNumber;
    this.filter.pageSize = storedPageSize;
  }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent): Promise<void> {
    // Important: pass all table cols (not only visible ones) to prevent hidden col filters from affecting results
    const currentFilters = this.filter.filters as any;

    const nextFilters = applyFiltersPanelChange(
      evt,
      currentFilters,
      this.allTableColumns
    );

    if (!nextFilters) {
      return;
    }

    this.filter.filters = nextFilters as any;

    const source = evt.source;
    const isApplyAction = source === 'apply';
    const isClearAction = source === 'clear';
    const shouldResetPaging = isApplyAction || isClearAction;

    if (shouldResetPaging) {
      const firstPageNumber = 1;

      this.pageNumber = firstPageNumber;
      this.filter.pageNumber = firstPageNumber;

      this.onCriteriaResetRequested();
    }

    this.initialLoadDone = true;
    await this.get();
  }

  private rebuildLocalDropdownOptions() {
    const typePipe = new ImportTypeListPipe(this.translateService);
    this.importTypeOptions = (typePipe as any).importTypesList ?? [];

    const statePipe = new ImportStatePipe(this.translateService);
    this.importStateOptions = (statePipe as any).importStatesList ?? [];
  }

  private rebuildFiltersPanelOverrides(): void {
    this.filtersPanelFieldOverrides = {
      type: {
        fieldLabel: 'TABLE.HEADER.ImportType',
        isMultiselect: true,

        options: this.importTypeOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },
      state: {
        fieldLabel: 'TABLE.HEADER.ImportState',
        isMultiselect: true,

        options: this.importStateOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      }
    };
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    const nextSortableColumns = visibleColumns
      .filter((col: any) => {
        const field = String(col?.field ?? '').trim();
        return (
          field.length > 0 &&
          field.toLowerCase() !== 'actions' &&
          col?.sortable !== false
        );
      })
      .map((col: any) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        let label;
        if (translated && translated !== translationKey) {
          label = translated;
        } else {
          label = col.header;
        }

        return {
          key: col.field,
          label
        };
      });

    this.sortableColumns = nextSortableColumns;

    // clear active sort if points to col that is not available due to system settings update
    const allowedKeys = new Set(nextSortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    }
  }

  goToUploadForm() {
    this.router.navigate(['imports/upload-data']);
  }

  isDownloadDisabled(status: string): boolean {
    return status !== ImportState.IMPORT_CREATED && status !== ImportState.IMPORT_SUCCEEDED;
  }

  async downloadImportedFile(event: any, importId: number) {
    this.importsService.apiImportsDownloadIdGet(importId)
      .subscribe({
        next: (response: any) => {
          const fileUrl = response.url;

          // Temp <a> to handle download
          const link = document.createElement('a');
          link.href = fileUrl;

          link.download = `import_${importId}`;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
        error: (err) => {
          console.error('Error during file download', err);
          console.error('Error during file download', err);
          this.toasterService.show('error', 'Error', err, 3000);
        }
      });
  }

  openImportDetails(event: any, importDetails: object) {
    this.modalboxDetails = importDetails;
    this.displayImportDetailsModalbox = true;
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
